"use client";

import React, {
  useState,
  useId,
  useEffect,
  ReactNode,
  KeyboardEvent,
} from "react";
import { Collapse } from "@mui/material";
import clsx from "clsx";
import { TransparentLoading } from "components/TransparentLoading";
import {
  SectionSkeleton,
  TSectionSkeleton,
  sectionSkeletonMinHeight,
} from "./SectionSkeleton";

export type TSectionLoadingMode = "auto" | "skeleton" | "overlay";

export interface CollapsibleSectionProps {
  /** Title displayed at the top of the section */
  title: string;
  /** Section content */
  children: ReactNode;
  /** Whether the section is collapsible */
  collapsible?: boolean;
  /** Default open state (only works when collapsible=true) */
  defaultOpen?: boolean;
  /** Optional class name for wrapper */
  className?: string;
  /** Optional class name for title row */
  headerClassName?: string;
  /** Optional class name for content area */
  contentClassName?: string;
  isLoading?: boolean;
  /**
   * Shape of the loading placeholder — set it to match what this section
   * wraps (`text` for detail panels, `table` for grids, `chart`, `map`).
   * Defaults to the neutral `block`.
   */
  skeleton?: TSectionSkeleton;
  /**
   * - `auto` (default) — skeleton until the section has shown content once,
   *   dimmed overlay for every load after that. First paint gets a placeholder
   *   shaped like the real thing; a refresh or poll keeps the data on screen
   *   instead of blanking it.
   * - `skeleton` — always replace the content while loading.
   * - `overlay` — always keep the content and dim it (the old behavior).
   */
  loadingMode?: TSectionLoadingMode;
  /**
   * Floor for the content area, applied loading or not, so the section doesn't
   * jump when the placeholder is swapped for real content. Defaults to the
   * skeleton variant's own height while loading.
   */
  minHeight?: number | string;
  /** Rows for a `text`/`table` skeleton, bars for `chart`. */
  skeletonRows?: number;
}

/**
 * A flexible section that can be either collapsible or static.
 *
 * Styling follows the SAMA catalog's section-heading motif: a navy title on a
 * pale navy band, led by a thick orange rule on the inline-start edge (the
 * right, since the app runs RTL). The rule is the Yoozmeters accent and is the
 * one place the brand orange appears in the app chrome.
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  collapsible = true,
  defaultOpen = true,
  className,
  headerClassName,
  contentClassName,
  isLoading,
  skeleton = "block",
  loadingMode = "auto",
  minHeight,
  skeletonRows,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  /**
   * Whether real content has ever been on screen. Seeded from the initial
   * `isLoading` so a section that mounts with data ready never flashes a
   * skeleton.
   */
  const [hasShownContent, setHasShownContent] = useState(!isLoading);
  useEffect(() => {
    if (!isLoading) setHasShownContent(true);
  }, [isLoading]);

  const showSkeleton =
    !!isLoading &&
    loadingMode !== "overlay" &&
    (loadingMode === "skeleton" || !hasShownContent);
  const showOverlay = !!isLoading && !showSkeleton;

  const handleToggle = () => {
    if (collapsible) setOpen((prev) => !prev);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!collapsible) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  const body = (
    <div
      id={contentId}
      className={clsx("p-4", contentClassName)}
      style={{
        minHeight:
          minHeight ??
          (showSkeleton ? sectionSkeletonMinHeight(skeleton) : undefined),
      }}
    >
      {showSkeleton ? (
        <SectionSkeleton
          variant={skeleton}
          minHeight={minHeight}
          rows={skeletonRows}
        />
      ) : (
        children
      )}
    </div>
  );

  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-lg border border-neutral-30 bg-white",
        className
      )}
    >
      {showOverlay && <TransparentLoading />}

      {/* Header — navy band with the orange accent rule on the leading edge */}
      <div
        className={clsx(
          "flex select-none items-center justify-between gap-3 px-4 py-2",
          "border-s-4 border-s-secondary-70 bg-primary-20 transition-colors duration-200",
          open && "border-b border-b-neutral-30",
          collapsible &&
            "cursor-pointer hover:bg-primary-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary-70",
          headerClassName
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? open : undefined}
        aria-controls={collapsible ? contentId : undefined}
      >
        <h2 className="text-sm font-bold text-primary-90">{title}</h2>

        {collapsible && (
          <span
            aria-hidden="true"
            className="flex h-6 w-6 shrink-0 items-center justify-center text-primary-70"
          >
            <i
              className={clsx(
                "fa-solid fa-chevron-down text-xs transition-transform duration-200",
                open && "-rotate-180"
              )}
            />
          </span>
        )}
      </div>

      {/* Content */}
      {collapsible ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {body}
        </Collapse>
      ) : (
        body
      )}
    </div>
  );
};

export default CollapsibleSection;
